import { useEffect, useState, useMemo } from 'react';
import { useSearchParams, Link, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { collection, getDocs } from 'firebase/firestore';
import { db, normalizeProduct } from '@/lib/firebase';
import { 
  Package, Search, X, ArrowLeft, ChevronLeft, ChevronRight, 
  Check, ShoppingBag, Plus, Minus, MessageCircle, Info, ShieldCheck 
} from 'lucide-react';
import ProductImage from '@/components/ProductImage';
import { useSite } from '@/lib/SiteContext';

// --- Animasyon Varyantları ---
const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100, damping: 15 } },
};

function formatPrice(price) {
  if (!price) return '';
  return Number(price).toLocaleString('tr-TR');
}

/* ─── PRODUCT DETAIL (ÜRÜN DETAY) ─── */
function ProductDetail() {
  const { id } = useParams();
  const { addToCart } = useSite();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeMedia, setActiveMedia] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isAdded, setIsAdded] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const urunlerSnap = await getDocs(collection(db, 'urunler'));
        let found = urunlerSnap.docs.find((d) => d.id === id);
        if (found) {
          const normalized = normalizeProduct(found);
          setProduct(normalized);
          if (normalized.variants?.length > 0) setSelectedVariant(normalized.variants[0]);
        }
      } catch (err) { console.log('Fetch error:', err); }
      finally { setLoading(false); }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (!product) return;
    const itemToAdd = {
      ...product,
      price: selectedVariant ? selectedVariant.price : product.price,
      selectedVariantName: selectedVariant ? selectedVariant.name : null
    };
    addToCart(itemToAdd, quantity);
    setIsAdded(true);
    setTimeout(() => setIsAdded(false), 2000);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-brand-cream">
      <div className="w-8 h-8 border-2 border-brand-dark/20 border-t-brand-dark rounded-full animate-spin" />
    </div>
  );

  if (!product) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-brand-cream gap-4">
      <Package size={48} className="text-brand-dark/20" />
      <p className="text-brand-dark/40">Ürün bulunamadı.</p>
      <Link to="/urunler" className="text-brand-dark text-sm underline">Ürünlere Dön</Link>
    </div>
  );

  const media = [...(product.images || []), ...(product.video || [])];
  const displayPrice = selectedVariant ? selectedVariant.price : product.price;

  return (
    <main className="min-h-screen bg-brand-cream pt-24 pb-20">
      <div className="container mx-auto px-4 max-w-7xl">
        <Link to="/urunler" className="inline-flex items-center gap-2 text-sm text-brand-dark/60 hover:text-brand-dark mb-8 transition-colors">
          <ArrowLeft size={16} /> Ürünlere Dön
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Sol: Medya Galerisi */}
          <div className="space-y-6">
            <div className="relative aspect-square rounded-[3rem] overflow-hidden bg-white shadow-2xl shadow-brand-dark/5 border border-slate-100">
              <ProductImage src={media[activeMedia]} alt={product.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
              {media.map((item, i) => (
                <button key={i} onClick={() => setActiveMedia(i)} className={`shrink-0 w-20 h-20 rounded-2xl overflow-hidden border-2 transition-all ${activeMedia === i ? 'border-brand-dark scale-105 shadow-md' : 'border-transparent opacity-50 hover:opacity-100'}`}>
                  <ProductImage src={item} alt="thumb" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Sağ: Ürün Bilgileri */}
          <div className="flex flex-col">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-dark/30 mb-4 block">{product.category || 'Özel Seri'}</span>
              <h1 className="text-4xl md:text-6xl font-extrabold text-brand-dark tracking-tighter mb-6 leading-tight">{product.name}</h1>
              
              <div className="flex items-center gap-4 mb-8">
                <span className="text-3xl font-bold text-brand-dark">{formatPrice(displayPrice)} TL</span>
                <span className="text-[10px] font-bold text-green-600 bg-green-50 px-3 py-1 rounded-full uppercase tracking-widest">Stokta Var</span>
              </div>

              <p className="text-brand-dark/60 leading-relaxed mb-10 text-lg border-l-2 border-brand-pink/30 pl-6">{product.description}</p>

              {/* Geri Getirilen: Ürün Özellikleri */}
              {product.features?.length > 0 && (
                <div className="mb-10">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-dark/40 mb-6 flex items-center gap-2">
                    <div className="w-4 h-[1px] bg-brand-dark/20" /> Ürün Özellikleri
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-8">
                    {product.features.map((feat, i) => (
                      <div key={i} className="flex items-start gap-3 group">
                        <div className="w-5 h-5 rounded-full bg-brand-pink/10 flex items-center justify-center shrink-0 group-hover:bg-brand-pink/20 transition-colors">
                          <div className="w-1.5 h-1.5 rounded-full bg-brand-pink" />
                        </div>
                        <span className="text-sm font-medium text-brand-dark/70 leading-tight">{feat}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Geri Getirilen: Teknik Detaylar */}
              {product.specifications?.length > 0 && (
                <div className="mb-12 p-8 rounded-[2rem] bg-white border border-slate-100 shadow-sm">
                  <h3 className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-dark/40 mb-6 flex items-center gap-2">
                    <Info size={14} className="text-brand-blue" /> Teknik Detaylar
                  </h3>
                  <div className="space-y-4">
                    {product.specifications.map((spec, i) => (
                      <div key={i} className="flex items-center justify-between text-sm border-b border-slate-50 pb-3 last:border-0 last:pb-0">
                        <span className="text-brand-dark/40 font-medium">{spec.split(':')[0]}</span>
                        <span className="text-brand-dark font-bold">{spec.split(':')[1] || spec}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Satın Alma Alanı */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex items-center justify-between border-2 border-slate-100 rounded-full p-1 bg-white sm:w-40">
                  <button onClick={() => setQuantity(q => Math.max(1, q - 1))} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-slate-50 text-brand-dark transition-colors"><Minus size={18} /></button>
                  <span className="font-bold text-lg">{quantity}</span>
                  <button onClick={() => setQuantity(q => q + 1)} className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-slate-50 text-brand-dark transition-colors"><Plus size={18} /></button>
                </div>
                <button onClick={handleAddToCart} disabled={isAdded} className={`flex-1 relative h-14 rounded-full font-bold text-lg transition-all active:scale-95 shadow-xl shadow-brand-dark/5 ${isAdded ? 'bg-green-500 text-white' : 'bg-brand-dark text-white hover:bg-brand-dark/90'}`}>
                  <AnimatePresence mode="wait">
                    {isAdded ? (
                      <motion.div key="added" initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="flex items-center justify-center gap-2"><Check size={20} /> Eklendi!</motion.div>
                    ) : (
                      <motion.div key="add" initial={{ y: 10, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="flex items-center justify-center gap-2"><ShoppingBag size={20} /> Sepete Ekle</motion.div>
                    )}
                  </AnimatePresence>
                </button>
              </div>

              {/* YENİ EKLENEN: MAY PLASTİK ÜRETİCİ GARANTİSİ */}
              <div className="mb-8 flex items-start gap-3 p-5 bg-brand-blue/5 rounded-3xl border border-brand-blue/10">
                <ShieldCheck className="text-brand-blue shrink-0 mt-0.5" size={22} />
                <div>
                  <h4 className="text-xs font-bold text-brand-dark mb-1 uppercase tracking-widest">Resmi Üretici Garantisi</h4>
                  <p className="text-xs text-brand-dark/60 font-medium leading-relaxed">
                    Bu ürün <strong className="text-brand-dark">May Plastik</strong> üretim ve kalite standartları altındadır. Yetkili satış kanalı güvencesiyle doğrudan üreticiden faturalandırılır.
                  </p>
                </div>
              </div>

              <a href={`https://wa.me/905542345454?text=Merhaba, ${product.name} hakkında bilgi almak istiyorum.`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 text-brand-dark/30 hover:text-brand-dark transition-colors font-bold text-xs uppercase tracking-widest group">
                <MessageCircle size={16} className="group-hover:text-green-500 transition-colors" /> Uzmana Soru Sor
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </main>
  );
}

/* ─── PRODUCTS LIST (ÜRÜN LİSTESİ) ─── */
function ProductsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [hoveredProductId, setHoveredProductId] = useState(null);

  const activeCategory = searchParams.get('kategori') || 'all';

  useEffect(() => {
    const fetchAllProducts = async () => {
      try {
        const urunlerSnap = await getDocs(collection(db, 'urunler'));
        const urunlerData = urunlerSnap.docs.map((doc) => normalizeProduct(doc));
        setProducts(urunlerData);
      } catch (err) { console.log('Fetch error:', err); }
      finally { setLoading(false); }
    };
    fetchAllProducts();
  }, []);

  const filteredProducts = products.filter((p) => {
    const matchCat = activeCategory === 'all' || (p.category || '') === activeCategory;
    const matchSearch = search === '' || (p.name || '').toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <main className="min-h-screen bg-brand-cream pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-7xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-12">
          <h1 className="text-5xl md:text-7xl font-extrabold text-brand-dark tracking-tighter">Ürünlerimiz</h1>
          <p className="text-brand-dark/40 mt-4 font-bold uppercase tracking-widest text-xs">AyzPlastik • Koleksiyon 2026</p>
        </motion.div>

        <div className="flex flex-col md:flex-row gap-6 mb-16 items-center justify-between">
          <div className="relative w-full max-w-md">
            <Search size={18} className="absolute left-5 top-1/2 -translate-y-1/2 text-brand-dark/20" />
            <input type="text" placeholder="Ürünlerde ara..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-full h-14 pl-14 pr-10 rounded-[1.5rem] border-none bg-white shadow-sm focus:ring-4 focus:ring-brand-dark/5 transition-all text-sm font-medium" />
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[...Array(8)].map((_, i) => <div key={i} className="aspect-square rounded-[2.5rem] bg-white animate-pulse" />)}
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={activeCategory + search} variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
              {filteredProducts.map((product) => {
                const thumbMedia = (product.images && product.images[0]);
                const isAnotherHovered = hoveredProductId !== null && hoveredProductId !== product.id;

                return (
                  <motion.div key={product.id} variants={itemVariants} onMouseEnter={() => setHoveredProductId(product.id)} onMouseLeave={() => setHoveredProductId(null)}>
                    <Link to={`/urunler/${product.id}`} className={`group block transition-all duration-500 ${isAnotherHovered ? 'opacity-30 scale-95 blur-[2px]' : 'opacity-100'}`}>
                      <div className="relative aspect-square rounded-[2.5rem] overflow-hidden bg-white shadow-sm border border-slate-100 group-hover:shadow-2xl transition-all duration-700">
                        <ProductImage src={thumbMedia} alt={product.name} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                      </div>
                      <div className="mt-6">
                        <h3 className="font-extrabold text-brand-dark text-lg leading-tight tracking-tight uppercase">{product.name}</h3>
                        <p className="text-brand-dark/40 text-sm font-bold mt-1 tracking-tighter">{formatPrice(product.price)} TL</p>
                      </div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </main>
  );
}

export { ProductsListPage, ProductDetail };